import { HOME_BACKGROUND_COPY as C, TV_BRAND_KIT_COPY, type TvPlatformKey } from '@/lib/constants/appFactory';
import { APP_STATIC_HOSTS } from '@/lib/constants/appPreview';
import type { AppCfgCoreRow, AppCfgsDto } from '@/lib/types/App';
import type { FactoryBrandKit } from '@/lib/types/AppFactory';

/**
 * Where the home background stands for an app:
 * - none: no background image anywhere — nothing to switch on
 * - light: the app uses the light theme; TV apps never get the image
 * - draft: the Brand Kit holds a background image that is not the app's home
 *   background yet — it becomes it with the next approval (the factory renders
 *   the home picture then, as uploaded)
 * - live: the app has a home background (from its Brand Kit, or from its
 *   earlier Roku channel)
 */
export type HomeBackgroundKind = 'none' | 'light' | 'draft' | 'live';

export interface HomeBackgroundView {
  kind: HomeBackgroundKind;
  /** The stored switch: absent / null (never chosen) reads as off. */
  checked: boolean;
  disabled: boolean;
  /** One sentence under the switch; null = nothing to add. */
  note: string | null;
  /** The live background came from the app's earlier Roku channel. */
  legacy: boolean;
  /** The image the preview draws: the Brand Kit's waiting one when there is one, else the live one. */
  previewUrl: string | null;
  previewIsDraft: boolean;
  /**
   * The live background is the darkened version from before design ADR-0016
   * (the TV apps now show the image as uploaded): it stays until the next
   * approval renders the picture.
   */
  liveRetired: boolean;
}

export interface HomeBackgroundInput {
  core: Pick<AppCfgCoreRow, 'homeBgEnabled' | 'homeBgKey' | 'homeBgSource'>;
  /** The app's newest Brand Kit revision; null = none. */
  kit: Pick<FactoryBrandKit, 'tileArtUrl' | 'homeBackgroundUrl'> | null;
  /** Drafts from the Brand Kit wait for approval. */
  hasDrafts: boolean;
  lightTheme: boolean;
  /** The white-label experience: images come from its own static host. */
  isReseller: boolean;
}

/** Pure rules of the home background switch on the Brand Kit tab (design ADR-0015 D2 storage, ADR-0016 D5 rendering). */
export class HomeBackgroundUtils {
  static readonly SOURCE_LEGACY = 'legacy_bg';
  /**
   * The derived, darkened home background the factory made before ADR-0016
   * (`home-background.jpg`, treatment 1; `home-background-2.jpg`, treatment 2 —
   * the demo's live key); the as-is render is `home-picture.jpg`. A kit
   * whose home version is the retired file has no picture yet: the preview
   * draws the image as uploaded, which is what the next approval renders.
   */
  static readonly RETIRED_DERIVED_BASENAME = /\/home-background(-\d+)?\.[a-z0-9]+$/i;

  /** The URL names the retired derived treatment, not the picture as uploaded. */
  static isRetiredDerived(url: string | null | undefined): boolean {
    return !!url && this.RETIRED_DERIVED_BASENAME.test(this.pathOf(url));
  }

  static staticBase(isReseller: boolean): string {
    return isReseller ? APP_STATIC_HOSTS.whitelabel : APP_STATIC_HOSTS.streamhoster;
  }

  /** The public URL of `appCfg.core.homeBgKey` on the account's static host; null without a key. */
  static keyUrl(key: string | null | undefined, isReseller: boolean): string | null {
    const k = (key ?? '').trim().replace(/^\/+/, '');
    return k ? `${this.staticBase(isReseller)}/${k}` : null;
  }

  /** A static-host URL moved to the account's own static host (both serve the same files); others pass through. */
  static brandUrl(url: string | null | undefined, isReseller: boolean): string | null {
    const u = (url ?? '').trim();
    if (!u) return null;
    for (const host of Object.values(APP_STATIC_HOSTS)) {
      if (u.startsWith(`${host}/`)) return this.staticBase(isReseller) + u.slice(host.length);
    }
    return u;
  }

  /** The object path of a static URL, so two hosts' URLs of one file compare equal. */
  private static pathOf(url: string): string {
    return url.replace(/^https?:\/\/[^/]+/i, '').replace(/[?#].*$/, '');
  }

  /** The app uses the light theme: the web row's dark-mode flag (dark when the app has no web row — the server's rule). */
  static isLightTheme(app: Pick<AppCfgsDto, 'web'>): boolean {
    return app.web != null && !app.web.style.styleIsDarkMode;
  }

  static view(input: HomeBackgroundInput): HomeBackgroundView {
    const { core, kit, hasDrafts, lightTheme, isReseller } = input;
    const checked = core.homeBgEnabled === true;
    const liveUrl = this.keyUrl(core.homeBgKey, isReseller);
    const legacy = core.homeBgSource === this.SOURCE_LEGACY && liveUrl != null;
    const liveRetired = this.isRetiredDerived(liveUrl);
    // The Brand Kit's picture: the factory's as-is render for this revision, else
    // the image as uploaded (the same picture, before the factory re-encodes it).
    const renderedUrl = this.isRetiredDerived(kit?.homeBackgroundUrl) ? null : this.brandUrl(kit?.homeBackgroundUrl, isReseller);
    const kitUrl = renderedUrl ?? this.brandUrl(kit?.tileArtUrl, isReseller);
    // Not the live one yet: nothing is live, or drafts wait with another image (a
    // revision without its render is never the live one — approval renders it).
    const sameAsLive = liveUrl != null && renderedUrl != null && this.pathOf(renderedUrl) === this.pathOf(liveUrl);
    const waiting = kitUrl != null && !sameAsLive && (liveUrl == null || hasDrafts);
    const base = { checked, legacy, liveRetired };

    if (lightTheme) {
      return { ...base, kind: 'light', disabled: true, note: C.noteLight, previewUrl: null, previewIsDraft: false };
    }
    if (liveUrl) {
      return {
        ...base,
        kind: 'live',
        disabled: false,
        note: waiting ? C.notePending : legacy ? C.noteLegacy : liveRetired ? C.noteRetired : null,
        previewUrl: waiting ? kitUrl : liveUrl,
        previewIsDraft: waiting,
      };
    }
    if (waiting) {
      return {
        ...base,
        kind: 'draft',
        disabled: false,
        note: hasDrafts ? C.noteDraft : C.noteDraftLater,
        previewUrl: kitUrl,
        previewIsDraft: true,
      };
    }
    return { ...base, kind: 'none', disabled: true, note: C.noteNone, previewUrl: null, previewIsDraft: false };
  }

  /**
   * The background image field's help: the ADR's sentence, naming only the TV
   * apps that are on (a Roku-only app never reads about the Fire TV home screen).
   */
  static fieldHelp(tv: readonly TvPlatformKey[]): string {
    const firetv = tv.includes('firetv');
    const googletv = tv.includes('googletv');
    if (firetv && googletv) return TV_BRAND_KIT_COPY.backgroundHelpBoth;
    if (firetv) return TV_BRAND_KIT_COPY.backgroundHelp;
    if (googletv) return TV_BRAND_KIT_COPY.backgroundHelpGoogleTv;
    return TV_BRAND_KIT_COPY.backgroundHelpRoku;
  }
}
